import React, { useState, useEffect, useRef } from 'react';

import { useAuth } from '../../../context/AuthContext';
import { useDevices } from '../../../context/DeviceContext';
import { produceSolutionMarkers } from '../../../DeviceMarkerFactory';
import { SideWays } from '../../../Sideways';
import { buttonStyle } from '../../../styles/constants';
import { TimeSpace } from '../../../TimeSpace';
import type { TracePoint, LatLng } from '../../../types';
import { useRedrawCounter } from '../../../utils/hooks';
import logger from '../../../utils/logger';
import { Button } from '../../common/Button';

/**
 * Processed trace point for map display
 */
interface MapTracePoint {
  id: string | number;
  title: string;
  latlng: LatLng;
  lat: number;
  lng: number;
  floor?: number;
  timestamp: Date | string;
  addedTimestamp?: Date | string;
  positionTime: Date | string;
  positionFiltered?: boolean;
  positionReplaced?: boolean;
  accuracy?: number;
  status?: string;
}

/**
 * Device information for display
 */
interface DeviceInfo {
  title: string;
  text: string;
  checkpoint: boolean;
  alarm: boolean;
  checked: boolean;
}

/**
 * Time filter step option
 */
interface TimeFilterStep {
  text: string;
  value: number;
}

/**
 * Props for the Trace component
 */
export interface TraceProps {
  /** Array of trace/position data points */
  trace?: TracePoint[];
  /** Map projection type ("timespace" or "sideways") */
  projection?: string;
  /** Map zoom level */
  zoom?: number;
  /** Function to update zoom level */
  setZoom?: (zoom: number) => void;
  /** Currently selected device ID */
  selectedDevice?: string | null;
  /** Function to close trace view */
  onClose: () => void;
}

/**
 * Trace Component - Device trace/path visualization
 *
 * Displays historical positioning data for a selected device.
 * Shows the path/trace of device movement over time with timestamps.
 * Supports filtering by time range and accuracy display.
 *
 * Features:
 * - Historical position tracking
 * - Time-based filtering
 * - Accuracy visualization
 * - Multiple projection modes
 * - Auto-close after timeout
 * - Device title editing (admin only)
 * - Checkpoint management
 */
export const Trace: React.FC<TraceProps> = ({
  trace = [],
  projection = 'timespace',
  zoom = 15,
  setZoom,
  selectedDevice,
  onClose,
}) => {
  const { isAdmin, isChecker } = useAuth();
  const { devices, currentCheckpoint, updateCurrentCheckpoint } = useDevices();

  // Local state
  const [selectedSolutionId, setSelectedSolutionId] = useState<string | null>(null);
  const [positionLimitS, setPositionLimitS] = useState(0);
  const [showAccuracy, setShowAccuracy] = useState(() => {
    const stored = window.localStorage.getItem('showAccuracy');
    return stored !== null ? stored === 'true' : true;
  });
  const [showPositionList, setShowPositionList] = useState(true);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [inactivityCountdown, setInactivityCountdown] = useState(30);

  // Refs for tracking state in event handlers (avoids stale closure issues)
  const lastActivityTimeRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const onCloseRef = useRef(onClose);

  // Keep onClose ref updated
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Auto-refresh for real-time updates - optimized with custom hook
  const redraw = useRedrawCounter(30000);

  // Inactivity timeout - close trace view after 2 minutes of inactivity
  useEffect(() => {
    logger.debug('Inactivity timer initialized');
    lastActivityTimeRef.current = Date.now();
    let checkInterval: NodeJS.Timeout | null = null;

    const resetActivity = (): void => {
      const now = Date.now();
      if (now - lastActivityTimeRef.current > 1000) {
        logger.debug('Activity detected, resetting inactivity timer');
      }
      lastActivityTimeRef.current = now;

      if (warningShownRef.current) {
        warningShownRef.current = false;
        setShowInactivityWarning(false);
        setInactivityCountdown(30);
      }
    };

    // Check inactivity every second
    checkInterval = setInterval(() => {
      const now = Date.now();
      const inactiveSeconds = Math.floor((now - lastActivityTimeRef.current) / 1000);

      // Show warning 30 seconds before closing (at 90 seconds of inactivity)
      if (inactiveSeconds >= 90 && inactiveSeconds < 120) {
        const remaining = 120 - inactiveSeconds;
        setInactivityCountdown(remaining);

        if (!warningShownRef.current) {
          warningShownRef.current = true;
          setShowInactivityWarning(true);
        }
      } else if (inactiveSeconds < 90 && warningShownRef.current) {
        warningShownRef.current = false;
        setShowInactivityWarning(false);
      }

      // Close after 2 minutes (120 seconds) of inactivity
      if (inactiveSeconds >= 120) {
        logger.debug(`Closing due to ${inactiveSeconds}s of inactivity`);
        onCloseRef.current();
      }
    }, 1000);

    // Listen for user activity events only (not scroll - can be triggered programmatically)
    window.addEventListener('mousemove', resetActivity);
    window.addEventListener('mousedown', resetActivity);
    window.addEventListener('keydown', resetActivity);
    window.addEventListener('touchstart', resetActivity);

    return () => {
      logger.debug('Cleaning up inactivity timers');
      if (checkInterval) clearInterval(checkInterval);
      window.removeEventListener('mousemove', resetActivity);
      window.removeEventListener('mousedown', resetActivity);
      window.removeEventListener('keydown', resetActivity);
      window.removeEventListener('touchstart', resetActivity);
    };
  }, []);

  /**
   * Handle "Stay" button click in inactivity warning
   */
  const handleStayClick = (): void => {
    logger.debug('User clicked Stay, resetting inactivity');
    lastActivityTimeRef.current = Date.now();
    warningShownRef.current = false;
    setShowInactivityWarning(false);
    setInactivityCountdown(30);
  };

  // Store showAccuracy preference
  useEffect(() => {
    window.localStorage.setItem('showAccuracy', showAccuracy.toString());
  }, [showAccuracy]);

  /**
   * Handle checkpoint selection
   */
  const handleSetCurrentCheckpoint = (checkpointId: string | null): void => {
    logger.debug(`Setting current checkpoint: ${checkpointId}`);
    updateCurrentCheckpoint(checkpointId);
  };

  /**
   * Get device information for the selected device
   */
  const getSelectedDeviceInfo = (): DeviceInfo => {
    const device = devices.find((d) => d.id === selectedDevice);
    if (!device) {
      return {
        title: 'Unknown Device',
        text: '',
        checkpoint: false,
        alarm: false,
        checked: false,
      };
    }

    return {
      title: device.title || 'Untitled Device',
      text: device.text || '',
      checkpoint: !!device.checkpoint,
      alarm: !!device.alarmTime,
      checked: !!device.checkinTime,
    };
  };

  /**
   * Process trace data for map display
   */
  const processTraceData = (): MapTracePoint[] => {
    const nowS = new Date().getTime() / 1000;
    const mapSeries: MapTracePoint[] = [];

    if (!Array.isArray(trace)) {
      return mapSeries;
    }

    trace.forEach((solution, index) => {
      // Handle different timestamp field names
      const timeField = solution.captureTime || solution.timestamp;
      if (!timeField) {
        logger.warn('No timestamp found in trace point:', solution);
        return;
      }

      const time = new Date(timeField);
      const ageS = nowS - time.getTime() / 1000;

      // Filter by age if limit is set
      if (positionLimitS && ageS > positionLimitS) {
        return;
      }

      // Ensure valid coordinates
      const lat = solution.lat;
      const lng = solution.lng;

      if (!lat || !lng) {
        logger.debug('Trace point without coordinates:', solution.id);
        return;
      }

      // Add to beginning of array (most recent first)
      mapSeries.unshift({
        id: solution.id || String(index),
        title: time.toLocaleString() + (solution.positionFiltered ? ' ~ ' : ''),
        latlng: [lat, lng],
        lat: lat,
        lng: lng,
        floor: solution.floor,
        timestamp: timeField,
        addedTimestamp: solution.addedTimestamp,
        positionTime: timeField,
        positionFiltered: solution.positionFiltered,
        positionReplaced: solution.positionReplaced,
        accuracy: solution.accuracy,
        status: solution.status,
      });
    });

    // Limit to 100 most recent positions
    return mapSeries.slice(0, 100);
  };

  const deviceInfo = getSelectedDeviceInfo();
  const mapSeries = processTraceData();
  const mapCenter: LatLng = mapSeries.length > 0 ? mapSeries[mapSeries.length - 1].latlng : [0, 0];

  /**
   * Time filter step options for position age filtering
   */
  const timeFilterSteps: TimeFilterStep[] = [
    { text: '1h', value: 3600 },
    { text: '6h', value: 6 * 3600 },
    { text: '1d', value: 24 * 3600 },
    { text: '3d', value: 3 * 24 * 3600 },
    { text: '∞', value: 0 },
  ];

  return (
    <div>
      {/* Main map container */}
      <div style={{ position: 'fixed', top: '0', left: '0', width: '100vw', height: '100vh' }}>
        {/* TimeSpace projection */}
        {mapSeries.length > 0 && (projection === 'timespace' || showAccuracy) && (
          <TimeSpace
            width="100vw"
            height="100vh"
            top="0"
            series={mapSeries}
            center={mapCenter}
            redraw={redraw}
            maxAge={positionLimitS || 3600 * 24}
            selected={selectedSolutionId}
            zoom={zoom}
            setZoom={setZoom}
            markerFactory={produceSolutionMarkers}
            showAccuracy={showAccuracy}
          />
        )}

        {/* SideWays projection */}
        {mapSeries.length > 0 && projection === 'sideways' && !showAccuracy && (
          <SideWays
            width="100vw"
            height="100vh"
            top="0"
            series={mapSeries}
            center={mapCenter}
            isChecker={isChecker}
            isAdmin={isAdmin}
            setCurrentCheckpoint={handleSetCurrentCheckpoint}
            currentCheckpoint={currentCheckpoint}
            redraw={redraw}
            maxAge={positionLimitS || 3600 * 24}
            selected={selectedSolutionId}
            setSelected={setSelectedSolutionId}
            zoom={zoom}
            setZoom={setZoom}
            markerFactory={produceSolutionMarkers}
            showAccuracy={showAccuracy}
          />
        )}

        {/* No trace data message */}
        {mapSeries.length === 0 && (
          <div
            style={{
              position: 'fixed',
              top: '40vh',
              left: '50%',
              transform: 'translateX(-50%)',
              textAlign: 'center',
              fontSize: '3vmin',
              color: '#666',
            }}
          >
            <h3>No trace data available</h3>
            <p>Device: {deviceInfo.title}</p>
            <p>No positioning data found for the selected time range.</p>
          </div>
        )}
      </div>

      {/* Device info header (left side) */}
      <div
        style={{
          position: 'fixed',
          top: '2vmin',
          left: '2vmin',
          background: 'rgba(255,255,255,0.9)',
          padding: '1vmin',
          borderRadius: '1vmin',
          fontSize: '2.5vmin',
          maxWidth: '40vw',
        }}
      >
        <div style={{ fontWeight: 'bold', marginBottom: '0.5vmin' }}>
          Trace View: {deviceInfo.title}
        </div>
        <div style={{ fontSize: '2vmin', color: '#666' }}>
          {deviceInfo.text && <div>ID: {deviceInfo.text}</div>}
          <div>Points: {mapSeries.length}</div>
          {deviceInfo.checkpoint && <div style={{ color: '#0a0' }}>📍 Checkpoint</div>}
          {deviceInfo.alarm && <div style={{ color: '#f00' }}>🚨 Alarm</div>}
          {deviceInfo.checked && <div style={{ color: '#00f' }}>✓ Checked</div>}
        </div>
      </div>

      {/* Position list panel (right side) */}
      <div
        style={{
          position: 'fixed',
          top: '2vmin',
          right: '2vmin',
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '1vmin',
          fontSize: '2vmin',
          width: showPositionList ? '30vw' : 'auto',
          maxWidth: '400px',
          minWidth: showPositionList ? '250px' : 'auto',
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header with close and toggle */}
        <div
          style={{
            padding: '1vmin',
            borderBottom: showPositionList ? '1px solid #ddd' : 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1vmin',
          }}
        >
          <Button
            text={showPositionList ? '▶' : '◀'}
            onClick={() => setShowPositionList(!showPositionList)}
            style={{
              background: '#eee',
              color: '#333',
              fontSize: '2vmin',
              padding: '0.5vmin 1vmin',
              minWidth: 'auto',
            }}
          />
          {showPositionList && (
            <span style={{ fontSize: '1.8vmin', color: '#666', flex: 1 }}>Position History</span>
          )}
          <Button
            text="✕ Close"
            onClick={onClose}
            style={{
              background: '#f44',
              color: 'white',
              fontSize: '2vmin',
              padding: '0.5vmin 1vmin',
            }}
          />
        </div>

        {/* Position list */}
        {showPositionList && (
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              maxHeight: '60vh',
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '1.6vmin',
              }}
            >
              <thead>
                <tr style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                  <th
                    style={{
                      padding: '0.5vmin 1vmin',
                      textAlign: 'left',
                      borderBottom: '1px solid #ddd',
                    }}
                  >
                    Capture time
                  </th>
                  <th
                    style={{
                      padding: '0.5vmin 1vmin',
                      textAlign: 'center',
                      borderBottom: '1px solid #ddd',
                    }}
                  >
                    Floor
                  </th>
                  <th
                    style={{
                      padding: '0.5vmin 1vmin',
                      textAlign: 'left',
                      borderBottom: '1px solid #ddd',
                    }}
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {mapSeries.map((point, index) => {
                  const time = new Date(point.timestamp);
                  const isSelected = selectedSolutionId === String(point.id);
                  return (
                    <tr
                      key={point.id}
                      onClick={() => setSelectedSolutionId(String(point.id))}
                      style={{
                        background: isSelected ? '#e3f2fd' : index % 2 === 0 ? '#fff' : '#fafafa',
                        cursor: 'pointer',
                        borderLeft: isSelected ? '3px solid #2196F3' : '3px solid transparent',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.background = '#f0f0f0';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected)
                          e.currentTarget.style.background = index % 2 === 0 ? '#fff' : '#fafafa';
                      }}
                    >
                      <td style={{ padding: '0.5vmin 1vmin', whiteSpace: 'nowrap' }}>
                        {time.toLocaleString()}
                      </td>
                      <td style={{ padding: '0.5vmin 1vmin', textAlign: 'center' }}>
                        {point.floor !== undefined ? point.floor : '-'}
                      </td>
                      <td style={{ padding: '0.5vmin 1vmin' }}>{point.status || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {mapSeries.length === 0 && (
              <div style={{ padding: '2vmin', textAlign: 'center', color: '#666' }}>
                No positions in selected time range
              </div>
            )}
          </div>
        )}
      </div>

      {/* Inactivity Warning */}
      {showInactivityWarning && (
        <div
          style={{
            position: 'fixed',
            bottom: '2vmin',
            right: '2vmin',
            padding: '1.5vmin',
            background: '#fff3e0',
            borderRadius: '0.5vmin',
            border: '2px solid #ff9800',
            zIndex: 1000,
            minWidth: '200px',
          }}
        >
          <div style={{ fontSize: '1.8vmin', fontWeight: 'bold', marginBottom: '1vmin' }}>
            Inactivity Warning
          </div>
          <div style={{ fontSize: '1.5vmin', marginBottom: '1vmin', color: '#666' }}>
            Closing in <strong>{inactivityCountdown}</strong> seconds...
          </div>
          <div style={{ display: 'flex', gap: '0.5vmin', justifyContent: 'center' }}>
            <Button
              text="Stay"
              onClick={handleStayClick}
              style={{
                background: '#4CAF50',
                color: 'white',
                fontSize: '1.5vmin',
                padding: '0.5vmin 1vmin',
              }}
            />
            <Button
              text="Close"
              onClick={onClose}
              style={{
                background: '#f44',
                color: 'white',
                fontSize: '1.5vmin',
                padding: '0.5vmin 1vmin',
              }}
            />
          </div>
        </div>
      )}

      {/* Time filter controls */}
      <div
        style={{
          position: 'fixed',
          bottom: '2vmin',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(255,255,255,0.9)',
          padding: '1vmin',
          borderRadius: '1vmin',
          maxWidth: '80vw',
        }}
      >
        <div style={{ fontSize: '2vmin', marginBottom: '0.5vmin', textAlign: 'center' }}>
          Time Range:
        </div>
        <div
          style={{
            display: 'flex',
            gap: '0.5vmin',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {timeFilterSteps.map((step, index) => (
            <Button
              key={index}
              text={step.text}
              onClick={() => setPositionLimitS(step.value)}
              style={{
                ...buttonStyle,
                fontSize: '1.8vmin',
                padding: '0.4vmin 0.8vmin',
                minWidth: '3vmin',
                background: positionLimitS === step.value ? '#4CAF50' : '#eee',
                color: positionLimitS === step.value ? 'white' : '#333',
                whiteSpace: 'nowrap',
              }}
            />
          ))}
        </div>
      </div>

      {/* Accuracy toggle (admin only) */}
      {isAdmin && (
        <div
          style={{
            position: 'fixed',
            bottom: '2vmin',
            right: '2vmin',
          }}
        >
          <Button
            text={showAccuracy ? 'Hide Accuracy' : 'Show Accuracy'}
            onClick={() => setShowAccuracy(!showAccuracy)}
            style={{
              background: showAccuracy ? '#4CAF50' : '#ccc',
              color: showAccuracy ? 'white' : '#333',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Trace;
